/**
 * Admin Template Service
 * 
 * Handles all admin template-related API calls.
 * 
 * @validates Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 */

import api from './api';
import {
  CategoryTemplate,
  CategoryTemplateTreeNode,
  CreateCategoryTemplateRequest,
  UpdateCategoryTemplateRequest,
  BroadcastResult,
  ApiResponse,
} from '@budgira/shared';

export const adminTemplateService = {
  /**
   * Get all templates as a tree structure
   * @validates Requirement 4.1
   */
  async getTemplates(): Promise<ApiResponse<CategoryTemplateTreeNode[]>> {
    const response = await api.get<ApiResponse<CategoryTemplateTreeNode[]>>('/admin/templates');
    return response.data;
  },

  /**
   * Get a specific template by ID
   * @validates Requirement 4.1
   */
  async getTemplate(id: string): Promise<ApiResponse<CategoryTemplate>> {
    const response = await api.get<ApiResponse<CategoryTemplate>>(`/admin/templates/${id}`);
    return response.data;
  },

  /**
   * Create a new template
   * @validates Requirement 4.2
   */
  async createTemplate(data: CreateCategoryTemplateRequest): Promise<ApiResponse<CategoryTemplate>> {
    const response = await api.post<ApiResponse<CategoryTemplate>>('/admin/templates', data);
    return response.data;
  },

  /**
   * Update a template
   * @validates Requirement 4.2
   */
  async updateTemplate(id: string, data: UpdateCategoryTemplateRequest): Promise<ApiResponse<CategoryTemplate>> {
    const response = await api.put<ApiResponse<CategoryTemplate>>(`/admin/templates/${id}`, data);
    return response.data;
  },

  /**
   * Delete a template
   * @validates Requirement 4.3
   */
  async deleteTemplate(id: string, recursive: boolean = false): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/admin/templates/${id}`, {
      params: { recursive },
    });
    return response.data;
  },

  /**
   * Broadcast a template to all users who don't have it
   * @validates Requirement 4.4
   */
  async broadcastTemplate(id: string): Promise<ApiResponse<BroadcastResult>> {
    const response = await api.post<ApiResponse<BroadcastResult>>(`/admin/templates/${id}/broadcast`);
    return response.data;
  },

  /**
   * Estimate the number of users affected by a broadcast
   * @validates Requirement 4.5
   */
  async estimateBroadcast(id: string): Promise<ApiResponse<{ count: number }>> {
    const response = await api.get<ApiResponse<{ count: number }>>(`/admin/templates/${id}/broadcast/estimate`);
    return response.data;
  },
};

export default adminTemplateService;
